import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import { IUser } from '../../types/damTypes';
import { hasPermission } from '../../utils/permissionUtils';
import PromptEditor from './components/PromptEditor';
import ReferencePicker from './components/ReferencePicker';
import CurrentGenerationPanel from './components/CurrentGenerationPanel';
import ResultsPanel from './components/ResultsPanel';
import GenerationDetailsDrawer from './components/GenerationDetailsDrawer';
import PublishCategoryDialog from './components/PublishCategoryDialog';
import RecipeRefreshPanel from './components/RecipeRefreshPanel';
import SalesSheetPanel from './components/SalesSheetPanel';
import PromptSettingsDialog from './components/PromptSettingsDialog';
import EditingPanel from './components/EditingPanel';
import WineImageSlots from './components/WineImageSlots';
import {
  AiImageArchiveCategory,
  AiImageEditSourceInput,
  AiImageGeneration,
  AiImageOutput,
  AiImagePreset,
  AiImagePublishSelection,
  AiImageReferenceInput,
  AiImageStudioConfig,
  AiImageStudioDraft,
  RecipeSearchAssetResult,
} from './types';
import { normalizeDraftForConfig, useAiImageStudioSession } from './hooks/useAiImageStudioSession';
import { usePasteReferenceImages } from './hooks/usePasteReferenceImages';
import { OPENAI_SUNBURST_MODEL_ID, getSelectableStudioModels, resolveSelectableModelId } from './modelCatalog';
import { getGenerationBlockedReason } from './studioPresentation';
import { galleryScopeForView, matchesAiImageGalleryScope } from './galleryScope';

interface AIImageStudioPageProps {
  onClose: () => void;
  currentUser: IUser | null;
  initialRecipeAssetId?: string | null;
  initialEditAssetId?: string | null;
}

type AiStudioView = 'create' | 'recipe' | 'wine' | 'sales' | 'edit' | 'review';
const WINE_DRAFT_STORAGE_KEY = 'ai-image-studio-wine-draft-v1';
const WINE_DEFAULT_PRESET_NAME = 'vinprompt master';

type WineImageSettings = Pick<AiImageStudioDraft, 'model' | 'aspectRatio' | 'imageSize' | 'quality' | 'outputFormat'>;

function pickConfigValue(values: string[] | undefined, preferred: string): string {
  if (!values?.length || values.includes(preferred)) return preferred;
  return values[0];
}

function createWineDefaultSettings(config: AiImageStudioConfig | null): WineImageSettings {
  return {
    model: OPENAI_SUNBURST_MODEL_ID,
    aspectRatio: pickConfigValue(config?.aspectRatios, '9:16'),
    imageSize: pickConfigValue(config?.imageSizes, '4K'),
    quality: pickConfigValue(config?.openAiQualities, 'high'),
    outputFormat: 'jpeg',
  };
}
const ARCHIVE_CATEGORY_OPTIONS: Array<{ value: '' | AiImageArchiveCategory; label: string }> = [
  { value: '', label: 'Alla' },
  { value: 'Produkt', label: 'Produkter' },
  { value: 'Recept', label: 'Recept' },
  { value: 'Miljö', label: 'Miljö' },
  { value: 'Blandat', label: 'Blandat' },
];
type PublishDialogState = {
  mode: 'approve' | 'publish';
  generationId: string;
  outputId: string;
} | null;

function buildRecipePrompt(recipe: RecipeSearchAssetResult, preset: AiImagePreset): string {
  const sections = [
    preset.promptTemplate.trim(),
    `Recept: ${recipe.recipeName || recipe.title}`,
    recipe.description ? `Beskrivning: ${recipe.description}` : '',
    recipe.ingredients ? `Ingredienser: ${recipe.ingredients}` : '',
    recipe.cuisine ? `Kök/stil: ${recipe.cuisine}` : '',
    recipe.mealType ? `Måltidstyp: ${recipe.mealType}` : '',
    recipe.season ? `Säsong: ${recipe.season}` : '',
    recipe.cookingMethod ? `Tillagning: ${recipe.cookingMethod}` : '',
    'Använd originalbilden som visuell referens för rättens form, uppläggning och huvudingredienser. Modernisera bilden utan att byta rätt eller lägga till irrelevanta ingredienser.',
  ];
  return sections.filter(Boolean).join('\n\n');
}

function shouldShowAiTemplates(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('aiMallar') === '1';
}

const AIImageStudioPage: React.FC<AIImageStudioPageProps> = ({
  onClose,
  currentUser,
  initialRecipeAssetId,
  initialEditAssetId,
}) => {
  const showAiTemplates = useMemo(() => shouldShowAiTemplates(), []);
  const [activeView, setActiveView] = useState<AiStudioView>(() => (shouldShowAiTemplates() ? 'sales' : 'create'));
  const [selectedGenerationId, setSelectedGenerationId] = useState<string | null>(null);
  const [selectedOutputId, setSelectedOutputId] = useState<string | null>(null);
  const [publishDialogState, setPublishDialogState] = useState<PublishDialogState>(null);
  const [recipeResults, setRecipeResults] = useState<RecipeSearchAssetResult[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeSearchAssetResult | null>(null);
  const [editSource, setEditSource] = useState<AiImageEditSourceInput | null>(null);
  const [recipeSearching, setRecipeSearching] = useState(false);
  const [handledInitialRecipeAssetId, setHandledInitialRecipeAssetId] = useState<string | null>(null);
  const [handledInitialEditAssetId, setHandledInitialEditAssetId] = useState<string | null>(null);
  const [handledDraftRecipeAssetId, setHandledDraftRecipeAssetId] = useState<string | null>(null);
  const [winePrompt, setWinePrompt] = useState('');
  const [winePresetId, setWinePresetId] = useState('');
  const [wineImageA, setWineImageA] = useState<AiImageReferenceInput | null>(null);
  const [wineImageB, setWineImageB] = useState<AiImageReferenceInput | null>(null);
  const [wineDraftHydrated, setWineDraftHydrated] = useState(false);
  const [wineSettings, setWineSettings] = useState<WineImageSettings>(() => createWineDefaultSettings(null));
  const winePresetChosenRef = useRef(false);
  const [promptSettingsOpen, setPromptSettingsOpen] = useState(false);

  const canUseStudio =
    hasPermission(currentUser, 'aiImages:use') ||
    hasPermission(currentUser, 'aiImages:review') ||
    hasPermission(currentUser, 'aiImages:publish') ||
    hasPermission(currentUser, 'aiImages:admin');
  const canReview =
    hasPermission(currentUser, 'aiImages:review') ||
    hasPermission(currentUser, 'aiImages:publish') ||
    hasPermission(currentUser, 'aiImages:admin');
  const canPublish =
    hasPermission(currentUser, 'aiImages:publish') ||
    hasPermission(currentUser, 'aiImages:admin');
  const canManageAssetVersions =
    hasPermission(currentUser, 'assets:manageVersions') ||
    hasPermission(currentUser, 'admin:all');

  const {
    config,
    presets,
    categories,
    history,
    galleryGenerations,
    setGalleryScope,
    archiveOutputs,
    historyFilters,
    setHistoryFilters,
    draft,
    setDraft,
    setDraftField,
    currentGeneration,
    loading,
    archiveLoading,
    archiveLoadingMore,
    archiveHasMore,
    archiveLoaded,
    generating,
    pendingGenerations,
    actionLoading,
    error,
    snackbar,
    closeSnackbar,
    notify,
    loadArchiveOutputs,
    reloadPresets,
    generate,
    generateFromDraft,
    applyPreset,
    addUploadedFiles,
    addDamAssetReference,
    addSkuProductReference,
    addGeneratedReference,
    removeReference,
    loadGenerationDetails,
    searchDamReferences,
    searchRecipeReferences,
    fetchDamReference,
    fetchRecipeReference,
    lookupProductReference,
    approveOutput,
    rejectOutput,
    publishOutput,
    replaceRecipeAsset,
    reuseAll,
    upscaleOutput,
    addComment,
    markRead,
    deleteOutput,
    deleteGeneration,
  } = useAiImageStudioSession(currentUser);

  const galleryScope = useMemo(() => galleryScopeForView(activeView), [activeView]);
  const visibleGeneration = currentGeneration && matchesAiImageGalleryScope(currentGeneration, galleryScope)
    ? currentGeneration
    : null;
  const visiblePending = useMemo(
    () => pendingGenerations.filter((item) => matchesAiImageGalleryScope(item, galleryScope)),
    [galleryScope, pendingGenerations],
  );

  useEffect(() => {
    setGalleryScope(galleryScope);
  }, [galleryScope, setGalleryScope]);

  usePasteReferenceImages({
    enabled: activeView === 'create' && !promptSettingsOpen && !publishDialogState,
    remainingSlots: Math.max((config?.maxReferences || 14) - draft.references.length, 0),
    onAddFiles: addUploadedFiles,
    onNotify: notify,
  });

  const selectedGeneration = useMemo(() => {
    if (!selectedGenerationId) return null;
    return history.find((generation) => generation._id === selectedGenerationId)
      || (currentGeneration?._id === selectedGenerationId ? currentGeneration : null);
  }, [currentGeneration, history, selectedGenerationId]);

  const selectedOutput = useMemo(() => {
    if (!selectedGeneration || !selectedOutputId) return null;
    return selectedGeneration.outputs.find((output) => output._id === selectedOutputId) || null;
  }, [selectedGeneration, selectedOutputId]);

  const generalPresets = useMemo(
    () => presets.filter((preset) => !preset.scope || preset.scope === 'general'),
    [presets]
  );

  const recipePresets = useMemo(
    () => presets.filter((preset) => preset.scope === 'recipe'),
    [presets]
  );

  const winePresets = useMemo(
    () => presets.filter((preset) => preset.scope === 'wine' && preset.isActive),
    [presets]
  );

  const isWineView = activeView === 'wine';

  useEffect(() => {
    if (isWineView) {
      setWineSettings(createWineDefaultSettings(config));
    }
  }, [config, isWineView]);

  const wineEditorDraft = useMemo(() => normalizeDraftForConfig({
    ...draft,
    ...wineSettings,
    count: 1,
    workflow: 'wine' as const,
    prompt: winePrompt,
    presetId: winePresetId,
    references: [wineImageA, wineImageB].filter((image): image is AiImageReferenceInput => Boolean(image)),
    promptTemplateId: undefined,
    templatePrompt: undefined,
    manualPromptEdited: false,
    targetAssetId: '',
    editMode: undefined,
    editSource: undefined,
    editMask: undefined,
  }, config), [config, draft, wineImageA, wineImageB, winePresetId, winePrompt, wineSettings]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(WINE_DRAFT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { prompt?: unknown; presetId?: unknown; presetChosen?: unknown };
        if (typeof parsed.prompt === 'string') setWinePrompt(parsed.prompt);
        if (typeof parsed.presetId === 'string') setWinePresetId(parsed.presetId);
        winePresetChosenRef.current = parsed.presetChosen === true
          || (typeof parsed.presetId === 'string' && parsed.presetId !== '');
      }
    } catch {
      // A damaged local draft should not block Vin Studio.
    } finally {
      setWineDraftHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!wineDraftHydrated) return;
    window.localStorage.setItem(WINE_DRAFT_STORAGE_KEY, JSON.stringify({
      prompt: winePrompt,
      presetId: winePresetId,
      presetChosen: winePresetChosenRef.current,
    }));
  }, [wineDraftHydrated, winePresetId, winePrompt]);

  useEffect(() => {
    if (!wineDraftHydrated || presets.length === 0) return;
    if (winePresetId && !winePresets.some((preset) => preset._id === winePresetId)) {
      setWinePresetId('');
    }
  }, [presets.length, wineDraftHydrated, winePresetId, winePresets]);

  const handleWinePresetChange = useCallback((presetId: string) => {
    winePresetChosenRef.current = true;
    if (!presetId) {
      setWinePresetId('');
      return;
    }
    const preset = winePresets.find((item) => item._id === presetId);
    if (!preset) {
      setWinePresetId('');
      return;
    }
    const template = preset.promptTemplate.trim();
    setWinePresetId(presetId);
    setWinePrompt((current) => {
      const base = current.trim();
      if (preset.appendMode === 'replace') return template;
      if (preset.appendMode === 'prepend') return [template, base].filter(Boolean).join('\n\n');
      return [base, template].filter(Boolean).join('\n\n');
    });
  }, [winePresets]);

  useEffect(() => {
    if (!wineDraftHydrated || winePresetChosenRef.current || winePresetId) return;
    const defaultPreset = winePresets.find((preset) => preset.name.trim().toLowerCase() === WINE_DEFAULT_PRESET_NAME);
    if (!defaultPreset) return;
    if (winePrompt.trim()) {
      winePresetChosenRef.current = true;
      setWinePresetId(defaultPreset._id);
      return;
    }
    handleWinePresetChange(defaultPreset._id);
  }, [handleWinePresetChange, wineDraftHydrated, winePresetId, winePresets, winePrompt]);

  const generationBlockedReason = useMemo(() => {
    if (activeView === 'wine') {
      return getGenerationBlockedReason({
        view: 'wine',
        prompt: winePrompt,
        hasImageA: Boolean(wineImageA),
        hasImageB: Boolean(wineImageB),
      });
    }
    if (activeView === 'recipe') {
      return getGenerationBlockedReason({
        view: 'recipe',
        prompt: draft.prompt,
        hasRecipe: Boolean(selectedRecipe),
      });
    }
    if (activeView === 'create') {
      return getGenerationBlockedReason({
        view: 'create',
        prompt: draft.prompt,
      });
    }
    return undefined;
  }, [activeView, draft.prompt, selectedRecipe, wineImageA, wineImageB, winePrompt]);

  const updateEditorSetting = (key: keyof WineImageSettings, value: string) => {
    if (activeView === 'wine') {
      setWineSettings((current) => ({ ...current, [key]: value }));
      return;
    }
    setDraftField(key, value);
  };

  const handleWineGenerate = useCallback(() => {
    if (!wineImageA || !wineImageB || !winePrompt.trim()) return;
    generateFromDraft({
      ...wineEditorDraft,
      workflow: 'wine',
      references: [wineImageA, wineImageB],
    });
  }, [generateFromDraft, wineEditorDraft, wineImageA, wineImageB, winePrompt]);

  const selectedRecipePreset = useMemo(
    () => recipePresets.find((preset) => preset._id === draft.presetId) || recipePresets[0] || null,
    [draft.presetId, recipePresets]
  );
  const activeRecipeTargetAssetId = selectedRecipe?._id || draft.targetAssetId || undefined;

  const applyRecipeToDraft = useCallback((recipe: RecipeSearchAssetResult, preset: AiImagePreset | null) => {
    if (!preset) return;
    const prompt = buildRecipePrompt(recipe, preset);
    setDraft((prev) => {
      const model = resolveSelectableModelId(prev.model, config) || prev.model;
      const modelConfig = getSelectableStudioModels(config).find((item) => item.id === model);
      return {
        ...prev,
        workflow: 'recipe_refresh',
        model,
        provider: modelConfig?.provider || prev.provider,
        targetAssetId: recipe._id,
        presetId: preset._id,
        promptTemplateId: preset._id,
        templatePrompt: preset.promptTemplate,
        manualPromptEdited: false,
        prompt,
        references: [
          {
            referenceId: `recipe-${recipe._id}`,
            type: 'dam_asset',
            assetId: recipe._id,
            url: recipe.previewUrl,
            thumbnailUrl: recipe.thumbnailUrl,
            title: recipe.recipeName || recipe.title,
            fileName: recipe.fileName,
            mimeType: recipe.fileType,
          },
        ],
      };
    });
  }, [config, setDraft]);

  useEffect(() => {
    if (!selectedGenerationId) return;
    const generationStillExists = history.some((generation) => generation._id === selectedGenerationId)
      || currentGeneration?._id === selectedGenerationId
      || archiveOutputs.some((item) => item.generationId === selectedGenerationId);
    if (!generationStillExists) {
      setSelectedGenerationId(null);
      setSelectedOutputId(null);
    }
  }, [archiveOutputs, currentGeneration, history, selectedGenerationId]);

  useEffect(() => {
    if (activeView === 'review' && !archiveLoaded && !archiveLoading) {
      void loadArchiveOutputs(undefined, false);
    }
  }, [activeView, archiveLoaded, archiveLoading, loadArchiveOutputs]);

  useEffect(() => {
    if (!selectedGeneration || !selectedOutputId) return;
    if (!selectedGeneration.outputs.some((output) => output._id === selectedOutputId)) {
      setSelectedOutputId(null);
    }
  }, [selectedGeneration, selectedOutputId]);

  useEffect(() => {
    if (!initialRecipeAssetId || handledInitialRecipeAssetId === initialRecipeAssetId || recipePresets.length === 0) {
      return;
    }

    let cancelled = false;
    const loadInitialRecipe = async () => {
      try {
        const recipe = await fetchRecipeReference(initialRecipeAssetId);
        if (cancelled) return;
        setSelectedRecipe(recipe);
        setRecipeResults([recipe]);
        setActiveView('recipe');
        applyRecipeToDraft(recipe, recipePresets[0]);
        setHandledInitialRecipeAssetId(initialRecipeAssetId);
      } catch {
        setHandledInitialRecipeAssetId(initialRecipeAssetId);
      }
    };

    void loadInitialRecipe();
    return () => {
      cancelled = true;
    };
  }, [applyRecipeToDraft, fetchRecipeReference, handledInitialRecipeAssetId, initialRecipeAssetId, recipePresets]);

  useEffect(() => {
    if (!initialEditAssetId || handledInitialEditAssetId === initialEditAssetId) {
      return;
    }

    let cancelled = false;
    const loadInitialEditAsset = async () => {
      try {
        const asset = await fetchDamReference(initialEditAssetId);
        if (cancelled) return;
        setEditSource({
          referenceId: `edit-dam-${asset._id}`,
          type: 'dam_asset',
          assetId: asset._id,
          url: asset.previewUrl,
          thumbnailUrl: asset.thumbnailUrl,
          title: asset.title,
          fileName: asset.fileName,
          mimeType: asset.fileType,
        });
        setActiveView('edit');
        setHandledInitialEditAssetId(initialEditAssetId);
      } catch {
        setHandledInitialEditAssetId(initialEditAssetId);
      }
    };

    void loadInitialEditAsset();
    return () => {
      cancelled = true;
    };
  }, [fetchDamReference, handledInitialEditAssetId, initialEditAssetId]);

  useEffect(() => {
    const targetAssetId = draft.workflow === 'recipe_refresh' ? draft.targetAssetId : '';
    if (!targetAssetId || selectedRecipe?._id === targetAssetId || handledDraftRecipeAssetId === targetAssetId) {
      return;
    }

    let cancelled = false;
    const loadDraftRecipe = async () => {
      try {
        const recipe = await fetchRecipeReference(targetAssetId);
        if (cancelled) return;
        setSelectedRecipe(recipe);
        setRecipeResults((prev) => (
          prev.some((item) => item._id === recipe._id)
            ? prev
            : [recipe, ...prev]
        ));
        setActiveView('recipe');
      } finally {
        if (!cancelled) {
          setHandledDraftRecipeAssetId(targetAssetId);
        }
      }
    };

    void loadDraftRecipe();
    return () => {
      cancelled = true;
    };
  }, [draft.targetAssetId, draft.workflow, fetchRecipeReference, handledDraftRecipeAssetId, selectedRecipe?._id]);

  const applyGenerationPromptToDraft = (generation: AiImageGeneration | null) => {
    if (!generation) return;
    setDraftField('prompt', generation.prompt || '');
    setDraftField('presetId', generation.presetId || '');
    setDraftField('workflow', generation.workflow || 'standard');
    setDraftField('targetAssetId', generation.targetAssetId || '');
    setDraftField('promptTemplateId', generation.promptTemplateId || '');
    setDraftField('templatePrompt', generation.templatePrompt || '');
    setDraftField('manualPromptEdited', Boolean(generation.manualPromptEdited));
    setDraftField('model', generation.model);
    setDraftField('aspectRatio', generation.aspectRatio);
    setDraftField('imageSize', generation.imageSize);
    setDraftField('count', generation.outputCount);
    setActiveView('create');
  };

  const handleReuseAll = async (generation: AiImageGeneration | null) => {
    if (!generation) return;
    const fullGeneration = activeView === 'review'
      ? (await loadGenerationDetails(generation._id)) || generation
      : generation;
    reuseAll(fullGeneration);
    setActiveView('create');
  };

  const handleOpenRecipeGeneration = async (generation: AiImageGeneration | null) => {
    if (!generation?.targetAssetId) return;
    const fullGeneration = activeView === 'review'
      ? (await loadGenerationDetails(generation._id)) || generation
      : generation;
    const recipe = await fetchRecipeReference(generation.targetAssetId);
    setSelectedRecipe(recipe);
    setRecipeResults((prev) => (
      prev.some((item) => item._id === recipe._id)
        ? prev
        : [recipe, ...prev]
    ));
    reuseAll(fullGeneration);
    setActiveView('recipe');
    setSelectedGenerationId(null);
    setSelectedOutputId(null);
  };

  const handleSelectOutput = async (generationId: string, outputId: string) => {
    setSelectedGenerationId(generationId);
    setSelectedOutputId(outputId);
    await loadGenerationDetails(generationId);
  };

  const handleEditGeneratedOutput = (generation: AiImageGeneration, output: AiImageOutput) => {
    setEditSource({
      referenceId: `edit-generated-${generation._id}-${output._id}`,
      type: 'generated',
      generationId: generation._id,
      outputId: output._id,
      url: output.publicUrl,
      thumbnailUrl: output.thumbnailUrl || output.mediumUrl || output.publicUrl,
      title: generation.prompt.slice(0, 80) || output.fileName,
      fileName: output.fileName,
      mimeType: output.mimeType,
    });
    setSelectedGenerationId(null);
    setSelectedOutputId(null);
    setActiveView('edit');
  };

  const handleHistoryFilterChange = (nextFilters: typeof historyFilters) => {
    if (nextFilters.search.trim()) {
      setActiveView('review');
    }
    setHistoryFilters(nextFilters);
  };

  const applyArchiveFilters = (nextFilters: typeof historyFilters) => {
    setHistoryFilters(nextFilters);
    setActiveView('review');
    void loadArchiveOutputs(nextFilters, false);
  };

  const handleRecipeSearch = async (query: string) => {
    setRecipeSearching(true);
    try {
      const results = await searchRecipeReferences(query);
      setRecipeResults(results);
    } finally {
      setRecipeSearching(false);
    }
  };

  const handleSelectRecipe = (recipe: RecipeSearchAssetResult) => {
    setSelectedRecipe(recipe);
    applyRecipeToDraft(recipe, selectedRecipePreset || recipePresets[0] || null);
  };

  const handleRecipePresetChange = (presetId: string) => {
    const preset = recipePresets.find((candidate) => candidate._id === presetId) || null;
    if (!preset) return;
    if (selectedRecipe) {
      applyRecipeToDraft(selectedRecipe, preset);
      return;
    }
    setDraft((prev) => ({
      ...prev,
      workflow: 'recipe_refresh',
      presetId: preset._id,
      promptTemplateId: preset._id,
      templatePrompt: preset.promptTemplate,
      manualPromptEdited: false,
      prompt: preset.promptTemplate,
    }));
  };

  const handleRecipePromptChange = (value: string) => {
    setDraft((prev) => ({
      ...prev,
      prompt: value,
      manualPromptEdited: prev.workflow === 'recipe_refresh'
        ? value.trim() !== String(prev.prompt || '').trim()
        : prev.manualPromptEdited,
    }));
  };

  const handleApplyHistoryFilters = async () => {
    setActiveView('review');
    await loadArchiveOutputs(undefined, false);
  };

  const handleRequestApprove = (generationId: string, outputId: string) => {
    setPublishDialogState({
      mode: 'approve',
      generationId,
      outputId,
    });
  };

  const handleRequestPublish = (generationId: string, outputId: string) => {
    setPublishDialogState({
      mode: 'publish',
      generationId,
      outputId,
    });
  };

  const handleConfirmPublishSelection = async (selection: AiImagePublishSelection) => {
    if (!publishDialogState) return;

    if (publishDialogState.mode === 'approve') {
      await approveOutput(publishDialogState.generationId, publishDialogState.outputId, selection);
    } else {
      await publishOutput(publishDialogState.generationId, publishDialogState.outputId, selection);
    }

    setPublishDialogState(null);
  };

  if (!canUseStudio) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ maxWidth: 620, width: '100%' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Du har inte behörighet att använda Athena AI Studio. Rollen behöver minst <strong>`aiImages:use`</strong>.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={onClose}>
              Stäng
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography>Laddar Athena AI Studio...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.25,
            px: 2,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'rgba(0,0,0,0.06)',
            bgcolor: 'background.paper',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onClose}
              sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 'auto' }}
            >
              Tillbaka
            </Button>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Athena AI Studio
            </Typography>
          </Box>

          {activeView === 'review' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                ml: 'auto',
              }}
            >
              <TextField
                size="small"
                label="Sök"
                value={historyFilters.search}
                onChange={(event) => handleHistoryFilterChange({ ...historyFilters, search: event.target.value })}
                placeholder="Prompt, SKU, metadata eller preset"
                sx={{ minWidth: { xs: '100%', md: 260 } }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    void handleApplyHistoryFilters();
                  }
                }}
              />
              <Button variant="outlined" onClick={() => void handleApplyHistoryFilters()} disabled={archiveLoading}>
                {archiveLoading ? 'Laddar...' : 'Sök'}
              </Button>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="ai-review-category-label">Kategorier</InputLabel>
                <Select
                  labelId="ai-review-category-label"
                  value={historyFilters.archiveCategory}
                  label="Kategorier"
                  onChange={(event) => {
                    const archiveCategory = String(event.target.value) as '' | AiImageArchiveCategory;
                    applyArchiveFilters({ ...historyFilters, archiveCategory });
                  }}
                >
                  {ARCHIVE_CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="ai-review-status-label">Status</InputLabel>
                <Select
                  labelId="ai-review-status-label"
                  value={historyFilters.status}
                  label="Status"
                  onChange={(event) => applyArchiveFilters({ ...historyFilters, status: String(event.target.value) })}
                >
                  <MenuItem value="">Alla</MenuItem>
                  <MenuItem value="completed">Klar</MenuItem>
                  <MenuItem value="approved">Godkänd</MenuItem>
                  <MenuItem value="rejected">Avslagen</MenuItem>
                  <MenuItem value="published_to_dam">Publicerad</MenuItem>
                  <MenuItem value="failed">Misslyckad</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="ai-review-model-label">Modell</InputLabel>
                <Select
                  labelId="ai-review-model-label"
                  value={historyFilters.model}
                  label="Modell"
                  onChange={(event) => applyArchiveFilters({ ...historyFilters, model: String(event.target.value) })}
                >
                  <MenuItem value="">Alla</MenuItem>
                  {(config?.knownModels || config?.models || []).map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {activeView !== 'review' && <Box sx={{ flex: 1 }} />}
          <Tooltip title="Hantera promptmallar">
            <IconButton
              aria-label="Hantera promptmallar"
              size="small"
              onClick={() => setPromptSettingsOpen(true)}
              sx={{
                color: 'primary.main',
                bgcolor: 'rgba(15,52,96,0.08)',
                borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(15,52,96,0.16)' },
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ px: 2, borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.06)', bgcolor: 'background.paper' }}>
          <Tabs
            value={activeView}
            onChange={(_, value: AiStudioView) => {
              setActiveView(value);
              if (value === 'create') {
                setDraft((prev) => ({
                  ...prev,
                  workflow: 'standard',
                  targetAssetId: '',
                  promptTemplateId: '',
                  templatePrompt: '',
                  manualPromptEdited: false,
                  presetId: generalPresets.some((preset) => preset._id === prev.presetId) ? prev.presetId : '',
                }));
              }
              if (value === 'recipe') {
                setDraftField('model', draft.model);
              }
              if (value !== 'review') {
                setSelectedGenerationId(null);
                setSelectedOutputId(null);
              }
            }}
            aria-label="Athena AI Studio tabs"
          >
            <Tab value="create" label="Skapa" />
            <Tab value="recipe" label="Förnya receptbild" />
            <Tab value="wine" label="Vin Studio" />
            {showAiTemplates && <Tab value="sales" label="AI Mallar" />}
            <Tab value="edit" label="Redigering" />
            <Tab value="review" label="Arkiv" />
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {error && (
            <Alert severity="error" sx={{ m: 2, mb: 0 }}>
              {error}
            </Alert>
          )}

          {activeView === 'edit' ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                gap: 0,
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(480px, 0.95fr) minmax(420px, 1.05fr)' },
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  overflow: 'auto',
                  px: 3.5,
                  py: 2.5,
                  borderRight: { lg: '1px solid' },
                  borderColor: { lg: 'divider' },
                  bgcolor: '#f3f4f6',
                }}
              >
                <EditingPanel
                  config={config}
                  categories={categories}
                  history={history}
                  editSource={editSource}
                  generating={generating}
                  onEditSourceChange={setEditSource}
                  onGenerateDraft={generateFromDraft}
                  onSearchDamAssets={searchDamReferences}
                  onLookupSkuReference={lookupProductReference}
                  onNotify={notify}
                />
              </Box>
              <Box
                sx={{
                  minHeight: 0,
                  overflow: 'auto',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CurrentGenerationPanel
                  generation={visibleGeneration}
                  recentGenerations={galleryGenerations}
                  pendingGenerations={visiblePending}
                  actionLoading={actionLoading}
                  onReuse={addGeneratedReference}
                  onUsePrompt={applyGenerationPromptToDraft}
                  onReuseAll={handleReuseAll}
                  onUpscale={upscaleOutput}
                  onEdit={handleEditGeneratedOutput}
                  onDeleteOutput={deleteOutput}
                  onReplaceRecipeAsset={replaceRecipeAsset}
                  onOpenRecipe={handleOpenRecipeGeneration}
                  recipeTargetAssetId={activeRecipeTargetAssetId}
                  canReplaceRecipeAsset={false}
                  maxOutputs={20}
                  onOpenArchive={() => setActiveView('review')}
                />
              </Box>
            </Box>
          ) : activeView === 'create' || activeView === 'recipe' || activeView === 'sales' || activeView === 'wine' ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                gap: 0,
                gridTemplateColumns: { xs: '1fr', lg: '540px minmax(0, 1fr)' },
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: { lg: '1px solid' },
                  borderColor: { lg: 'divider' },
                  bgcolor: '#e5e7eb',
                }}
              >
                {activeView === 'sales' ? (
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3.5, py: 2.5 }}>
                    <SalesSheetPanel
                      config={config}
                      categories={categories}
                      generating={generating}
                      onSearchDamAssets={searchDamReferences}
                      onGenerateDraft={generateFromDraft}
                    />
                  </Box>
                ) : (
                  <PromptEditor
                    config={config}
                    presets={activeView === 'wine' ? winePresets : generalPresets}
                    showPreset={activeView !== 'recipe'}
                    presetLabel={activeView === 'wine' ? 'Vinprompt' : 'Promptmall'}
                    blockedReason={generationBlockedReason}
                    draft={activeView === 'recipe' ? { ...draft, presetId: '' } : activeView === 'wine' ? wineEditorDraft : draft}
                    generating={generating}
                    onModelChange={(value) => updateEditorSetting('model', value)}
                    onPromptChange={activeView === 'recipe'
                      ? handleRecipePromptChange
                      : activeView === 'wine'
                        ? setWinePrompt
                        : (value) => setDraftField('prompt', value)}
                    onPresetChange={(presetId) => {
                      if (activeView === 'wine') {
                        handleWinePresetChange(presetId);
                        return;
                      }
                      if (!presetId) {
                        setDraftField('presetId', '');
                        return;
                      }
                      applyPreset(presetId);
                    }}
                    onAspectRatioChange={(value) => updateEditorSetting('aspectRatio', value)}
                    onImageSizeChange={(value) => updateEditorSetting('imageSize', value)}
                    onQualityChange={(value) => updateEditorSetting('quality', value)}
                    onOutputFormatChange={(value) => updateEditorSetting('outputFormat', value)}
                    onCountChange={(value) => {
                      if (activeView !== 'wine') setDraftField('count', value);
                    }}
                    onGenerate={() => {
                      if (activeView === 'wine') {
                        handleWineGenerate();
                        return;
                      }
                      generate();
                    }}
                    onResetPrompt={activeView === 'recipe' && selectedRecipe
                      ? () => applyRecipeToDraft(selectedRecipe, selectedRecipePreset || recipePresets[0] || null)
                      : undefined}
                    resetPromptLabel="Återställ till receptmall"
                  >
                    {activeView === 'recipe' ? (
                      <RecipeRefreshPanel
                        recipePresets={recipePresets}
                        selectedRecipe={selectedRecipe}
                        selectedPresetId={selectedRecipePreset?._id || ''}
                        searchResults={recipeResults}
                        searching={recipeSearching}
                        onSearch={handleRecipeSearch}
                        onSelectRecipe={handleSelectRecipe}
                        onPresetChange={handleRecipePresetChange}
                      />
                    ) : activeView === 'wine' ? (
                      <WineImageSlots
                        imageA={wineImageA}
                        imageB={wineImageB}
                        onChange={(slot, image) => {
                          if (slot === 'a') setWineImageA(image);
                          else setWineImageB(image);
                        }}
                        onNotify={notify}
                      />
                    ) : (
                      <ReferencePicker
                        references={draft.references}
                        history={history}
                        categories={categories}
                        maxReferences={config?.maxReferences || 14}
                        onAddUploadedFiles={addUploadedFiles}
                        onSearchDamAssets={searchDamReferences}
                        onLookupSkuReference={lookupProductReference}
                        onAddDamAssetReference={addDamAssetReference}
                        onAddSkuProductReference={addSkuProductReference}
                        onAddGeneratedReference={addGeneratedReference}
                        onRemoveReference={removeReference}
                        onReorderReferences={(orderedIds) => {
                          setDraft((prev) => {
                            const byId = new Map(prev.references.map((reference) => [reference.referenceId, reference]));
                            const next = orderedIds
                              .map((id) => byId.get(id))
                              .filter((reference): reference is AiImageReferenceInput => Boolean(reference));
                            if (next.length !== prev.references.length) return prev;
                            return { ...prev, references: next };
                          });
                        }}
                      />
                    )}
                  </PromptEditor>
                )}
              </Box>

              <Box
                sx={{
                  minHeight: 0,
                  overflow: 'auto',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CurrentGenerationPanel
                  generation={visibleGeneration}
                  recentGenerations={galleryGenerations}
                  pendingGenerations={visiblePending}
                  actionLoading={actionLoading}
                  onReuse={addGeneratedReference}
                  onUsePrompt={applyGenerationPromptToDraft}
                  onReuseAll={handleReuseAll}
                  onUpscale={upscaleOutput}
                  onEdit={handleEditGeneratedOutput}
                  onDeleteOutput={deleteOutput}
                  onReplaceRecipeAsset={replaceRecipeAsset}
                  onOpenRecipe={handleOpenRecipeGeneration}
                  recipeTargetAssetId={activeRecipeTargetAssetId}
                  canReplaceRecipeAsset={activeView === 'recipe' && canPublish && canManageAssetVersions}
                  maxOutputs={20}
                  onOpenArchive={() => setActiveView('review')}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 1 }}>
              {archiveLoading && archiveOutputs.length === 0 && (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              )}
              {(!archiveLoading || archiveOutputs.length > 0) && (
                <ResultsPanel
                  generations={history}
                  archiveOutputs={archiveOutputs}
                  actionLoading={actionLoading}
                  onApproveRequest={handleRequestApprove}
                  onReject={rejectOutput}
                  onPublishRequest={handleRequestPublish}
                  onReuse={addGeneratedReference}
                  onUsePrompt={applyGenerationPromptToDraft}
                  onReuseAll={handleReuseAll}
                  onUpscale={upscaleOutput}
                  onOpenRecipe={handleOpenRecipeGeneration}
                  onSelectOutput={handleSelectOutput}
                  onDeleteGeneration={deleteGeneration}
                  onLoadMore={() => loadArchiveOutputs(undefined, true)}
                  archiveHasMore={archiveHasMore}
                  archiveLoadingMore={archiveLoadingMore}
                  canReview={canReview}
                  canPublish={canPublish}
                  onEdit={handleEditGeneratedOutput}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      <GenerationDetailsDrawer
        generation={selectedGeneration}
        selectedOutput={selectedOutput}
        open={activeView === 'review' && Boolean(selectedOutput)}
        actionLoading={actionLoading}
        canReview={canReview}
        canPublish={canPublish}
        onClose={() => {
          setSelectedOutputId(null);
          setSelectedGenerationId(null);
        }}
        onApproveRequest={handleRequestApprove}
        onReject={rejectOutput}
        onPublishRequest={handleRequestPublish}
        onReuse={addGeneratedReference}
        onUpscale={upscaleOutput}
        onEdit={handleEditGeneratedOutput}
        onUsePrompt={() => applyGenerationPromptToDraft(selectedGeneration)}
        onReuseAll={() => handleReuseAll(selectedGeneration)}
        onOpenRecipe={() => handleOpenRecipeGeneration(selectedGeneration)}
        onAddComment={addComment}
        onMarkRead={markRead}
        currentUserId={currentUser?._id}
      />

      <PublishCategoryDialog
        open={Boolean(publishDialogState)}
        title={publishDialogState?.mode === 'approve' ? 'Godkänn och publicera till DAM' : 'Publicera till DAM'}
        categories={categories}
        submitting={
          Boolean(
            publishDialogState
            && actionLoading === `${publishDialogState.mode}:${publishDialogState.outputId}`
          )
        }
        onClose={() => setPublishDialogState(null)}
        onConfirm={handleConfirmPublishSelection}
      />

      <PromptSettingsDialog
        open={promptSettingsOpen}
        initialTab={activeView === 'wine' ? 'wine' : activeView === 'recipe' ? 'recipe' : 'general'}
        onClose={() => setPromptSettingsOpen(false)}
        onPresetsChanged={async () => {
          await reloadPresets();
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AIImageStudioPage;
